import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { formatDuration, formatViews, getCategoryName } from "@/utils/video";

interface HeroCarouselProps {
  items: Video[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused || items.length <= 1) return;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % items.length);
    }, 6500);
    return () => window.clearInterval(timer);
  }, [paused, items.length]);

  if (items.length === 0) return null;

  const current = items[index];

  const goPrev = () => setIndex((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setIndex((prev) => (prev + 1) % items.length);

  return (
    <section
      className="relative w-full overflow-hidden bg-background-950"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      aria-label="焦点视频推荐"
    >
      <div className="relative h-[540px] w-full md:h-[620px]">
        {items.map((video, i) => (
          <div
            key={video.id}
            className={`absolute inset-0 transition-opacity duration-[900ms] ease-out ${
              i === index ? "opacity-100" : "opacity-0"
            }`}
            aria-hidden={i !== index}
          >
            <img
              src={video.cover}
              alt={`${video.title} 焦点封面`}
              title={`${video.title} - 亚洲激情视频焦点推荐`}
              className="h-full w-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/80 to-background-950/25" />
            <div className="absolute inset-0 bg-gradient-to-t from-background-950 via-background-950/20 to-background-950/70" />
          </div>
        ))}

        <div className="relative z-10 mx-auto flex h-full w-full max-w-[1440px] flex-col justify-center px-4 pb-20 md:px-8 md:pb-0">
          <h1 className="mb-5 flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] text-primary-300">
            <span className="h-px w-8 bg-primary-500" />
            亚洲激情视频 · Asia Passion
          </h1>

          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-primary-600/60 bg-primary-950/70 px-3 py-1 text-[11px] text-primary-200">
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-fire-line text-[11px]" />
              </span>
              本周编辑精选
            </span>

            <h2 className="mt-4 font-heading text-3xl font-bold leading-snug text-foreground-50 md:text-5xl">
              {current.title}
            </h2>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-300">
              <span className="text-primary-300">{getCategoryName(current.category)}</span>
              <span className="h-1 w-1 rounded-full bg-foreground-600" />
              <span>{current.type}</span>
              <span className="h-1 w-1 rounded-full bg-foreground-600" />
              <span className="tabular-nums">{formatDuration(current.duration)}</span>
              <span className="h-1 w-1 rounded-full bg-foreground-600" />
              <span className="tabular-nums">{formatViews(current.views)}</span>
            </div>

            <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-foreground-300 md:text-base">
              {current.summary}
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link
                to={`/detail/${current.id}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-500"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-play-fill text-base" />
                </span>
                立即观看
              </Link>
              <Link
                to={`/category/${current.category}`}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-600 bg-background-950/40 px-6 py-3 text-sm font-medium text-foreground-100 backdrop-blur-sm transition-colors hover:border-primary-500 hover:text-primary-200"
              >
                浏览同类内容
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-arrow-right-line text-sm" />
                </span>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-6 left-0 right-0 z-10 mx-auto flex w-full max-w-[1440px] items-center justify-between px-4 md:bottom-8 md:px-8">
          <div className="flex items-center gap-2">
            {items.map((video, i) => (
              <button
                key={video.id}
                type="button"
                onClick={() => setIndex(i)}
                aria-label={`切换到第 ${i + 1} 个推荐`}
                aria-current={i === index}
                className={`h-1 cursor-pointer rounded-full transition-all duration-300 ${
                  i === index ? "w-8 bg-primary-400" : "w-4 bg-foreground-600 hover:bg-foreground-400"
                }`}
              />
            ))}
            <span className="ml-2 text-[11px] tabular-nums text-foreground-400">
              {String(index + 1).padStart(2, "0")} / {String(items.length).padStart(2, "0")}
            </span>
          </div>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={goPrev}
              aria-label="上一个推荐"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-600 bg-background-950/60 text-foreground-200 backdrop-blur-sm transition-colors hover:border-primary-500 hover:text-primary-200"
            >
              <i className="ri-arrow-left-line text-base" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="下一个推荐"
              className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-600 bg-background-950/60 text-foreground-200 backdrop-blur-sm transition-colors hover:border-primary-500 hover:text-primary-200"
            >
              <i className="ri-arrow-right-line text-base" />
            </button>
          </div>
        </div>
      </div>

      <div className="border-t border-background-800 bg-background-900">
        <div className="mx-auto grid w-full max-w-[1440px] grid-cols-2 gap-px px-4 md:grid-cols-4 md:px-8">
          {[
            { icon: "ri-film-line", value: "30 部", label: "示例片目" },
            { icon: "ri-layout-grid-line", value: "6 个", label: "内容分类" },
            { icon: "ri-time-line", value: "每周", label: "持续更新" },
            { icon: "ri-shield-check-line", value: "18+", label: "成年用户" },
          ].map((stat) => (
            <div key={stat.label} className="flex items-center gap-3 py-4 md:py-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-800">
                <i className={`${stat.icon} text-base text-primary-300`} />
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-100">{stat.value}</p>
                <p className="text-[11px] text-foreground-500">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}